// UIC Country Helper
// Helper for working with UIC (International Union of Railways) country codes
// UIC codes are the first 2 digits of station IDs and identify the country

/**
 * Mapping of UIC country code prefixes (first 2 digits of station ID) to Wikipedia language codes
 * Based on the main language of each country
 */
export const wikipediaLanguageCode: Record<string, string> = {
  "10": "fi", // Finland
  "20": "ru", // Russia
  "21": "be", // Belarus (Belarusian)
  "22": "uk", // Ukraine (Ukrainian)
  "23": "ro", // Moldova (Romanian)
  "24": "lt", // Lithuania
  "25": "lv", // Latvia
  "26": "et", // Estonia
  "27": "kk", // Kazakhstan
  "28": "ka", // Georgia
  "29": "uz", // Uzbekistan
  "41": "sq", // Albania
  "42": "ja", // Japan
  "44": "bs", // Bosnia and Herzegovina
  "49": "bs", // Bosnia and Herzegovina
  "50": "bs", // Bosnia and Herzegovina
  "51": "pl", // Poland
  "52": "bg", // Bulgaria
  "53": "ro", // Romania
  "54": "cs", // Czech Republic
  "55": "hu", // Hungary
  "56": "sk", // Slovakia
  "57": "az", // Azerbaijan
  "58": "hy", // Armenia
  "60": "en", // Ireland (English)
  "61": "ko", // South Korea
  "62": "sr", // Montenegro (Serbian)
  "65": "mk", // North Macedonia
  "70": "en", // United Kingdom
  "71": "es", // Spain
  "72": "sr", // Serbia
  "73": "el", // Greece
  "74": "sv", // Sweden
  "75": "tr", // Turkey
  "76": "no", // Norway
  "78": "hr", // Croatia
  "79": "sl", // Slovenia
  "80": "de", // Germany
  "81": "de", // Austria (German)
  "82": "fr", // Luxembourg (French - most common for Wikipedia)
  "83": "it", // Italy
  "84": "nl", // Netherlands
  "85": "de", // Switzerland (German - largest language group)
  "86": "da", // Denmark
  "87": "fr", // France
  "88": "nl", // Belgium (Dutch/French - using Dutch)
  "94": "pt", // Portugal
  "95": "he", // Israel
};

/**
 * Mapping of UIC country code prefixes to country flag emojis
 */
export const countryFlag: Record<string, string> = {
  "10": "🇫🇮", // Finland
  "20": "🇷🇺", // Russia
  "21": "🇧🇾", // Belarus
  "22": "🇺🇦", // Ukraine
  "23": "🇲🇩", // Moldova
  "24": "🇱🇹", // Lithuania
  "25": "🇱🇻", // Latvia
  "26": "🇪🇪", // Estonia
  "27": "🇰🇿", // Kazakhstan
  "28": "🇬🇪", // Georgia
  "29": "🇺🇿", // Uzbekistan
  "41": "🇦🇱", // Albania
  "42": "🇯🇵", // Japan
  "44": "🇧🇦", // Bosnia and Herzegovina
  "49": "🇧🇦", // Bosnia and Herzegovina
  "50": "🇧🇦", // Bosnia and Herzegovina
  "51": "🇵🇱", // Poland
  "52": "🇧🇬", // Bulgaria
  "53": "🇷🇴", // Romania
  "54": "🇨🇿", // Czech Republic
  "55": "🇭🇺", // Hungary
  "56": "🇸🇰", // Slovakia
  "57": "🇦🇿", // Azerbaijan
  "58": "🇦🇲", // Armenia
  "60": "🇮🇪", // Ireland
  "61": "🇰🇷", // South Korea
  "62": "🇲🇪", // Montenegro
  "65": "🇲🇰", // North Macedonia
  "70": "🇬🇧", // United Kingdom
  "71": "🇪🇸", // Spain
  "72": "🇷🇸", // Serbia
  "73": "🇬🇷", // Greece
  "74": "🇸🇪", // Sweden
  "75": "🇹🇷", // Turkey
  "76": "🇳🇴", // Norway
  "78": "🇭🇷", // Croatia
  "79": "🇸🇮", // Slovenia
  "80": "🇩🇪", // Germany
  "81": "🇦🇹", // Austria
  "82": "🇱🇺", // Luxembourg
  "83": "🇮🇹", // Italy
  "84": "🇳🇱", // Netherlands
  "85": "🇨🇭", // Switzerland
  "86": "🇩🇰", // Denmark
  "87": "🇫🇷", // France
  "88": "🇧🇪", // Belgium
  "94": "🇵🇹", // Portugal
  "95": "🇮🇱", // Israel
};

/**
 * Extracts the Wikipedia language code from a station ID based on UIC country codes
 * @param stationId - The station ID (UIC format, first 2 digits are country code)
 * @returns The Wikipedia language code (e.g., "de", "fr", "en"), or null if not found
 */
export function getWikipediaLanguageCode(stationId: string): string | null {
  if (stationId.length < 2) return null;
  const prefix = stationId.substring(0, 2);
  return wikipediaLanguageCode[prefix] || null;
}

/**
 * Gets the country flag emoji for a station ID based on UIC country codes
 * @param stationId - The station ID (UIC format, first 2 digits are country code)
 * @returns The country flag emoji, or null if not found
 */
export function getCountryFlag(stationId: string): string | null {
  if (stationId.length < 2) return null;
  const prefix = stationId.substring(0, 2);
  return countryFlag[prefix] || null;
}

